import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { OverviewSection, PeopleSection, rangeToParams } from "../Settings/ProjectMetrics";
import { DeliverySection, QualitySection, ReadinessSection } from "../Settings/ProjectInsights";
import { analyticsPanels as extensionPanels } from "@afriannotate/extensions";
import { ReviewQueueBody } from "../Settings/ReviewQueue";
import { DateRangePicker } from "../../components/charts/DateRangePicker";
import { TabHeader } from "../../components/TabHeader/TabHeader";
import { AnalyticsPanel } from "./AnalyticsPanel";
import { cn } from "../../utils/bem";
import { ProjectNav } from "./ProjectNav";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ProjectAnalytics.prefix.css";

const block = cn("project-analytics");

/**
 * A project's Analytics page: one strip of sections (Overview, Quality,
 * Readiness, Delivery, People, Review queue) under a title row that
 * carries the period, with each section opening on its figures and
 * then cards in two columns. A section other than Overview is kept in
 * the URL hash so a link or a reload lands on it. Old links (#metrics, #insights,
 * #review) still land somewhere sensible.
 */
const SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "quality", label: "Quality" },
  { key: "readiness", label: "Readiness" },
  { key: "delivery", label: "Delivery" },
  { key: "people", label: "People" },
  { key: "review", label: "Review queue" },
];
const OLD_HASHES = { metrics: "overview", insights: "quality" };

export const ProjectAnalytics = () => {
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);
  const history = useHistory();
  const location = useLocation();
  const api = useAPI();

  const hash = (location.hash || "").replace(/^#/, "");
  const wanted = OLD_HASHES[hash] ?? hash;
  const [section, setSection] = useState(SECTIONS.some((s) => s.key === wanted) ? wanted : "overview");

  useEffect(() => {
    // Overview is the page itself (no hash); every other section is named.
    const newHash = section === "overview" ? "" : `#${section}`;
    if ((location.hash || "") !== newHash) {
      history.replace({ pathname: location.pathname, search: location.search, hash: newHash });
    }
    // Also re-asserted when something else rewrites the URL without the
    // hash (the People table's page parameter does).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, location.hash]);

  // A link to another section while the page is open (a card's link, the
  // browser's back button) switches the section too.
  useEffect(() => {
    if (wanted && wanted !== section && SECTIONS.some((s) => s.key === wanted)) setSection(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);

  // One period for the whole page; the sections with a time axis use it.
  const [range, setRange] = useState({ key: "30d", days: 30, bucket: "day" });
  const rangeParams = useMemo(() => rangeToParams(range), [range]);

  // Live count of open items, as a badge on Review queue.
  const [openCount, setOpenCount] = useState(null);
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    api
      .callApi("projectReviewQueue", {
        params: { pk: project.id, status: "open", page_size: 1 },
        suppressError: true,
      })
      .then((res) => {
        if (cancelled) return;
        if (res && !res.error) setOpenCount(res.status_counts?.open ?? res.count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [api, project?.id]);

  if (!project?.id) return null;

  // An installed extension's panels for a section, rendered with the
  // page's own card so they match the platform's.
  const panels = (key) => {
    const mine = (extensionPanels ?? []).filter((p) => (p.section ?? "overview") === key);
    if (!mine.length) return null;
    return mine.map(({ key: id, component: Component }) => (
      <Component key={id} project={project} rangeParams={rangeParams} Panel={AnalyticsPanel} />
    ));
  };

  return (
    <div className={block.toClassName()}>
      <TabHeader
        title="Analytics"
        subtitle={t("pages.projectdashboard.project_analytics.triage_flagged_work_in_the_review_queue_")}
        actions={<DateRangePicker value={range.key} custom onChange={setRange} />}
      />

      <div className={block.elem("tabs").toClassName()} role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={section === s.key}
            className={block
              .elem("tab")
              .mod({ active: section === s.key })
              .toClassName()}
            onClick={() => setSection(s.key)}
          >
            {s.label}
            {s.key === "review" && openCount != null && openCount > 0 && (
              <span className={block.elem("badge").toClassName()}>{openCount > 99 ? "99+" : openCount}</span>
            )}
          </button>
        ))}
      </div>

      <div className={block.elem("body").toClassName()} role="tabpanel">
        {section === "overview" && (
          <OverviewSection project={project} rangeParams={rangeParams} extra={panels("overview")} />
        )}
        {section === "quality" && <QualitySection project={project} extra={panels("quality")} />}
        {section === "readiness" && <ReadinessSection project={project} extra={panels("readiness")} />}
        {section === "delivery" && <DeliverySection project={project} extra={panels("delivery")} />}
        {section === "people" && <PeopleSection project={project} rangeParams={rangeParams} extra={panels("people")} />}
        {section === "review" && <ReviewQueueBody project={project} />}
      </div>
    </div>
  );
};

ProjectAnalytics.title = "Analytics";
ProjectAnalytics.path = "/analytics";
ProjectAnalytics.exact = true;

ProjectAnalytics.context = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return <ProjectNav projectId={project.id} canManage={canManage} project={project} userRole={role} />;
};
